import { useCallback, useEffect, useRef } from "react";
import { useAuthContext } from "providers/Auth";
import {
  createFilterPreference,
  updateFilterPreference
} from "api/user-filter-preferences";

/** How long the filters have to stand still before a save goes out. */
const SAVE_DEBOUNCE_MS = 800;

const sameSelection = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b);

/**
 * Keeps one screen's filter selection on the partner user, so leaving the page
 * and coming back lands on the filters it was left with.
 *
 * Reading costs nothing: the saved selection already arrived on the auth
 * payload, so `initial` is available on the very first render and the screen
 * can seed its state from it rather than mounting unfiltered and correcting
 * itself a frame later. A successful save patches that payload back, so the
 * next visit in the same session seeds from what was just chosen rather than
 * from whatever was there at boot.
 *
 * Writing is debounced and diffed. Picking four properties one at a time is one
 * save, not four, and a render that produces an identical selection — which
 * happens whenever an unrelated piece of page state changes — writes nothing.
 *
 * The row is unique on (user, context): the first save creates it and every
 * later one patches the id that came back, so a user can never accumulate rows
 * for the same screen.
 *
 * Saving is deliberately quiet. It is a convenience, not an action the user
 * asked for, so a failure leaves the page working and un-nagged; the selection
 * is simply not remembered next time.
 */
export const useSavedFilters = <T>(
  context: string,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  const { auth, patchAuth } = useAuthContext();
  const saved = auth?.filter_preferences?.[context];

  // Captured once. Later renders must not re-seed the screen, or a save would
  // bounce back and overwrite what the user is in the middle of choosing.
  const initialRef = useRef<T | undefined>(saved?.filters as T | undefined);
  const preferenceIdRef = useRef<number | undefined>(saved?.id);

  // Seeded with what was loaded, so a mount that changes nothing saves nothing.
  const lastSavedRef = useRef<unknown>(initialRef.current);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const inFlightRef = useRef(false);
  /** Selection waiting on the debounce, flushed if the screen closes first. */
  const pendingRef = useRef<T | undefined>();
  const queuedRef = useRef<T | undefined>();

  // Read at save time rather than closed over, so writing back the other
  // screens' preferences does not make `persist` churn on every auth change.
  const allPreferencesRef = useRef(auth?.filter_preferences);
  allPreferencesRef.current = auth?.filter_preferences;

  const persist = useCallback(
    async (selection: T) => {
      if (inFlightRef.current) {
        queuedRef.current = selection;
        return;
      }
      inFlightRef.current = true;
      try {
        const filters = selection as Record<string, unknown>;
        const id =
          preferenceIdRef.current === undefined
            ? (await createFilterPreference(context, filters)).id
            : (await updateFilterPreference(preferenceIdRef.current, filters))
                .id;

        preferenceIdRef.current = id;
        lastSavedRef.current = selection;

        // The auth payload is fetched once at app boot, so without this the
        // in-memory copy still holds what was saved *last* session. Leaving
        // and coming back re-mounts this hook, which would then seed the
        // screen from that stale copy — the filters would not take effect
        // until a full page load. Patching keeps it current with no extra
        // request; the write has already succeeded by here.
        patchAuth({
          filter_preferences: {
            ...allPreferencesRef.current,
            [context]: { id, filters }
          }
        });
      } catch {
        // Convenience only — see above. Leave lastSavedRef alone so the next
        // change tries again.
      } finally {
        inFlightRef.current = false;
        const queued = queuedRef.current;
        queuedRef.current = undefined;
        if (
          queued !== undefined &&
          !sameSelection(queued, lastSavedRef.current)
        )
          persistRef.current(queued);
      }
    },
    [context, patchAuth]
  );

  const persistRef = useRef(persist);
  persistRef.current = persist;

  const save = useCallback(
    (selection: T) => {
      if (!enabled) return;

      clearTimeout(timerRef.current);
      pendingRef.current = undefined;

      if (inFlightRef.current) {
        queuedRef.current = selection;
        return;
      }
      if (sameSelection(selection, lastSavedRef.current)) return;

      pendingRef.current = selection;
      timerRef.current = setTimeout(() => {
        pendingRef.current = undefined;
        persist(selection);
      }, SAVE_DEBOUNCE_MS);
    },
    [enabled, persist]
  );

  // Flush rather than discard on the way out. Changing a filter and leaving
  // the page inside the debounce window is the normal way to use this — the
  // change is what you want remembered, and dropping it would make the feature
  // look unreliable precisely when it was used deliberately. Read through refs
  // so this runs on unmount only, not every time `persist` changes identity.
  useEffect(
    () => () => {
      clearTimeout(timerRef.current);
      if (pendingRef.current !== undefined)
        persistRef.current(pendingRef.current);
    },
    []
  );

  return { initial: initialRef.current, save };
};
